import { isString } from "lodash";

/**
 * Event type
 */
export interface BaseEvent {
  type: string
  [key: string]: any;
}

/**
 * Action function
 */
export type Action<C, E extends BaseEvent, S extends string> =
  (ctx: C, event: E, state: S) => any

/**
 * Transition definition
 */
export interface Transition<C, E extends BaseEvent, S extends string> {
  target: S
  guard?: (ctx: C, event: E) => boolean | Promise<boolean>
  actions?: Array<Action<C, E, S> | string>
  metaData?: any
}

/**
 * State definition
 */
export interface StateObject<C, E extends BaseEvent, S extends string> {
  entry?: (ctx: C, event: E) => any
  exit?: (ctx: C, event: E) => any
  on: Record<string, Transition<C, E, S>>
}

/**
 * State machine configuration
 */
export interface StateMachineConfig<C, E extends BaseEvent, S extends string> {
  initial: S
  context: C
  states: Record<S, StateObject<C, E, S>>
  actions?: Record<string, Action<C, E, S>>
}

/**
 * A simple generic state machine
 */
class StateMachine<
  C,
  E extends BaseEvent,
  S extends string
> {
  private config: StateMachineConfig<C, E, S>;
  private state: S;
  private context: C;
  private actions: Record<string, Action<C, E, S>>;

  constructor(config: StateMachineConfig<C, E, S>) {
    this.config = config;
    this.state = config.initial;
    this.context = { ...config.context };
    this.actions = { ...config.actions };
  }

  /**
   * Perform a transition based on the given event
   */
  async transition(event: E): Promise<void> {
    const currentState = this.config.states[this.state];
    // No further transitions if we are in a final state
    if (!currentState) {
      throw new Error(`${this.state} is a final state.`);
    }

    const transition = currentState.on?.[event.type];
    if (!transition) return;

    const { target, guard, actions } = transition;

    let guardCheck = true;
    if (guard) {
      guardCheck = Boolean(await guard(this.context, event));
    }
    if (!guardCheck) return;

    if (currentState.exit) {
      await currentState.exit(this.context, event);
    }

    if (actions) {
      for (let action of actions) {
        if (isString(action)) {
          action = this.actions[action];
        }
        await action(this.context, event, this.state);
      }
    }

    this.state = target;

    const nextState = this.config.states[this.state];
    if (nextState.entry) {
      await nextState.entry(this.context, event);
    }
  }
}

export default StateMachine;
